import { AREAS, getLineLabel } from '../../config/roasterySpec';

const formatCurrency = (v) => new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(v || 0);

function resolveLabel(areaConfigs, areaCode, lineNumber) {
  if (lineNumber != null) {
    const cfg = areaConfigs.find((c) => c.AreaCode === areaCode && c.LineNumber === lineNumber && c.UnitNumber == null);
    if (cfg?.CustomLabel) return cfg.CustomLabel;
  }
  const cfg = areaConfigs.find((c) => c.AreaCode === areaCode && c.LineNumber == null && c.UnitNumber == null);
  if (cfg?.CustomLabel) return cfg.CustomLabel;
  return null;
}

function DeltaBadge({ delta }) {
  if (Math.abs(delta) < 0.01) return null;
  const color = delta > 0 ? '#e67e22' : '#27ae60';
  return (
    <span style={{ fontSize: '12px', fontWeight: 600, color }}>
      {delta > 0 ? '+' : ''}
      {formatCurrency(delta)}
    </span>
  );
}

function ItemDot({ type }) {
  const colors = { added: '#2ecc40', removed: '#e74c3c', changed: '#f5a623' };
  return (
    <span
      style={{
        display: 'inline-block',
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        background: colors[type] || '#ccc',
        flexShrink: 0,
        marginTop: '5px'
      }}
    />
  );
}

function TypeLabel({ type }) {
  const map = { added: { label: 'Added', color: '#2ecc40' }, removed: { label: 'Removed', color: '#e74c3c' }, changed: { label: 'Modified', color: '#f5a623' } };
  const t = map[type] || { label: type, color: '#999' };
  return <span style={{ fontSize: '10px', fontWeight: 600, color: t.color, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{t.label}</span>;
}

export default function CompareView({ areas = [], versionA, versionB, totalDelta, totalAdded = 0, totalRemoved = 0, totalChanged = 0, areaConfigs = [], loading }) {
  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '28px', height: '28px', border: '3px solid #e5e5e5', borderTopColor: '#1a1a1a', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          <div style={{ fontSize: '13px', color: '#999' }}>Comparing versions...</div>
        </div>
      </div>
    );
  }

  if (!versionA || !versionB) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#999', fontSize: '14px' }}>Select two versions to compare</p>
      </div>
    );
  }

  // Sort areas by per-quotation SortOrder override, falling back to AREAS order
  const getAreaOrder = (code) => {
    const config = (areaConfigs || []).find((c) => c.AreaCode === code && c.LineNumber == null && c.UnitNumber == null);
    const parsed = Number.parseInt(config?.SortOrder, 10);
    return Number.isInteger(parsed) ? parsed : AREAS[code]?.order ?? 99;
  };
  const sortedAreas = [...areas].sort((a, b) => getAreaOrder(a.areaCode) - getAreaOrder(b.areaCode));
  const hasChanges = totalAdded + totalRemoved + totalChanged > 0 || Math.abs(totalDelta || 0) >= 0.01 || sortedAreas.length > 0;

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '32px 40px', background: '#fafafa' }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        {/* Header card */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', border: '1px solid #eee', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h2 style={{ margin: '0 0 6px 0', fontSize: '17px', fontWeight: 600, color: '#1a1a1a' }}>
                v{versionA.VersionNumber} &rarr; v{versionB.VersionNumber}
              </h2>
              <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
                {totalAdded > 0 && <span style={{ padding: '2px 8px', borderRadius: '10px', background: '#eafbef', color: '#1a8c42', fontWeight: 600 }}>{totalAdded} added</span>}
                {totalRemoved > 0 && <span style={{ padding: '2px 8px', borderRadius: '10px', background: '#fdf2f2', color: '#c0392b', fontWeight: 600 }}>{totalRemoved} removed</span>}
                {totalChanged > 0 && <span style={{ padding: '2px 8px', borderRadius: '10px', background: '#fef9f0', color: '#b7791f', fontWeight: 600 }}>{totalChanged} modified</span>}
                {!hasChanges && <span style={{ color: '#999' }}>No differences</span>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '13px', color: '#999' }}>{formatCurrency(versionA.total)}</span>
                <span style={{ fontSize: '13px', color: '#bbb' }}>&rarr;</span>
                <span style={{ fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>{formatCurrency(versionB.total)}</span>
              </div>
              {totalDelta !== 0 && (
                <div style={{ marginTop: '2px' }}>
                  <DeltaBadge delta={totalDelta} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Empty state */}
        {!hasChanges && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '48px 24px', border: '1px solid #eee', textAlign: 'center' }}>
            <p style={{ color: '#999', fontSize: '14px', margin: 0 }}>No differences found between these versions.</p>
          </div>
        )}

        {/* Area sections */}
        {sortedAreas.map((area) => {
          const areaLabel = resolveLabel(areaConfigs, area.areaCode, null) || AREAS[area.areaCode]?.label || area.areaCode;

          return (
            <div key={area.areaCode} style={{ marginBottom: '16px' }}>
              {/* Area header */}
              <div
                style={{
                  background: 'white',
                  borderRadius: '10px',
                  padding: '14px 18px',
                  border: '1px solid #eee',
                  marginBottom: '6px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>{areaLabel}</div>
                    <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                      {[area.added > 0 && `${area.added} added`, area.removed > 0 && `${area.removed} removed`, area.changed > 0 && `${area.changed} modified`].filter(Boolean).join(', ') ||
                        'Price adjustment'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', justifyContent: 'flex-end' }}>
                      <span style={{ fontSize: '12px', color: '#bbb' }}>{formatCurrency(area.totalA)}</span>
                      <span style={{ fontSize: '11px', color: '#ddd' }}>&rarr;</span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>{formatCurrency(area.totalB)}</span>
                    </div>
                    <DeltaBadge delta={area.totalDelta} />
                  </div>
                </div>
              </div>

              {/* Line-level content changes (no line-level price display) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '12px' }}>
                {area.lines
                  .filter((line) => Array.isArray(line.items) && line.items.length > 0)
                  .map((line) => {
                    const lineLabel = resolveLabel(areaConfigs, area.areaCode, line.lineNumber) || getLineLabel(line.lineNumber);

                    return (
                      <div
                        key={line.lineNumber}
                        style={{
                          background: 'white',
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #f0f0f0'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 500, color: '#444' }}>{lineLabel}</div>
                        </div>

                        {/* Item descriptions (no pricing) */}
                        {line.items.length > 0 && (
                          <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {line.items.map((item, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                <ItemDot type={item.type} />
                                <span
                                  style={{
                                    fontSize: '11px',
                                    color: item.type === 'removed' ? '#aaa' : '#666',
                                    textDecoration: item.type === 'removed' ? 'line-through' : 'none',
                                    lineHeight: '17px'
                                  }}
                                >
                                  {item.description || 'Item'}
                                </span>
                                <TypeLabel type={item.type} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
